import "./GroupCardShared.scss";

import type { FC } from "react";
import { useBoolean } from "usehooks-ts";

import { PageGroupInfoCard } from "components/materials/page/groups/PageGroupInfoCard";
import { JoinGroupButton } from "components/materials/page/groups/buttons/JoinGroupButton";
import {
  hasReadiedUsersReachedMaxGroupSize,
  hasTotalUsersReachedMaxGroupSize,
  isPageGroupUserInPageGroup,
} from "components/materials/page/groups/helpers";
import { PageType } from "components/server-types";
import { t } from "i18n/i18n";
import { Banner } from "mds/components/Banner";
import { IconText } from "mds/components/IconText";
import { SquareButton } from "mds/components/buttons/SquareButton";
import { AddPlusIcon } from "mds/icons";
import { storeApi, useAppSelector } from "store/index";
import { NestedPageGroupType, selectCurrentSectionUserId } from "store/selectors";

interface StudentSelfSelectCardProps {
  pageGroups: NestedPageGroupType[];
  page: PageType;
}

const MAX_EMPTY_GROUPS_ALLOWED = 2;

export const StudentSelfSelectCard: FC<StudentSelfSelectCardProps> = ({ pageGroups, page }) => {
  const sectionUserId = useAppSelector(selectCurrentSectionUserId);
  const { value: isFetching, setTrue: setFetching, setFalse: setNotFetching } = useBoolean(false);

  const pageGroupUsers = pageGroups.map((pageGroup) => pageGroup.page_group_users).flat();

  const userPageGroupUser = pageGroupUsers.find(
    (pageGroupUser) => pageGroupUser.section_user_id === sectionUserId,
  );

  // We are striving to find a balance of when to show the Add Group button.
  // We want to avoid too many empty groups, but also to expedite the process
  // of students adding new groups (given the API calls and updates have a lag time, which students complained about).
  // For now we allow up to 2 empty groups and will re-evaluate this logic in the future.
  const numberOfEmptyGroups = pageGroups.filter(
    (pageGroup) => pageGroup.page_group_users.length === 0,
  ).length;
  const shouldShowAddGroup = !page.locked_at && numberOfEmptyGroups < MAX_EMPTY_GROUPS_ALLOWED;

  const onChangeGroup = async (pageGroupId: string) => {
    if (!sectionUserId) {
      return;
    }
    setFetching();
    if (userPageGroupUser) {
      // If the current user is already in a group, we just need to update their
      // page_group_id when they join or create a new group.
      await storeApi.page_group_users.partial_update({
        id: userPageGroupUser.id,
        page_group_id: pageGroupId,
        section_user_id: sectionUserId,
      });
    } else {
      // If the current user isn't in any group yet (i.e. don't have a pageGroupUser),
      // we need to create a pageGroupUser for the user.
      await storeApi.page_group_users.create({
        page_group_id: pageGroupId,
        section_user_id: sectionUserId,
        section_id: page.section_id,
      });
    }
    setNotFetching();
  };

  const onConfirmGroup = async () => {
    if (!userPageGroupUser) return;
    setFetching();

    await storeApi.page_group_users.partial_update({
      id: userPageGroupUser.id,
      is_ready: true,
    });

    setNotFetching();
  };

  return (
    <div className="group-card flex w-full flex-col gap-4 rounded-2xl border border-black-tint-90 bg-black-tint-97 px-5 py-4">
      <div>
        <div className="h2 text-black-tint-20">
          {t("student_self_select_card.join_group_below")}
        </div>
        {userPageGroupUser && (
          <div className="body-m text-black-tint-20">
            {t("student_self_select_card.cannot_change_group_after_start")}
          </div>
        )}
      </div>

      {page.locked_at ? (
        <Banner kind="info">{t("lock_status.group_card_lock_warning")}</Banner>
      ) : (
        pageGroups.map((pageGroup, index) => {
          const isSelected = Boolean(
            isPageGroupUserInPageGroup(userPageGroupUser ?? null, pageGroup),
          );
          return (
            <PageGroupInfoCard
              isActive={isSelected}
              key={pageGroup.id}
              pageGroup={pageGroup}
              pageGroupIndex={index}
            >
              <JoinGroupButton
                disabled={
                  isSelected
                    ? hasReadiedUsersReachedMaxGroupSize(pageGroup, page)
                    : hasTotalUsersReachedMaxGroupSize(pageGroup, page)
                }
                index={index}
                isLoading={isFetching}
                isSelected={isSelected}
                kind={isSelected ? "success" : userPageGroupUser ? "tertiary" : "accent"}
                onChangeGroup={() => onChangeGroup(pageGroup.id)}
                onConfirm={onConfirmGroup}
              />
            </PageGroupInfoCard>
          );
        })
      )}

      {shouldShowAddGroup && (
        <SquareButton
          kind="ghost"
          size="s"
          onClick={() => {
            storeApi.page_groups
              .create({
                page_id: page.id,
                section_id: page.section_id,
              })
              .then((res) => {
                onChangeGroup(res.id);
              });
          }}
        >
          <IconText iconStart={<AddPlusIcon />} text={t("common.add.page_group")} />
        </SquareButton>
      )}
    </div>
  );
};
